import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import type { CashCapacity } from '@/app/services/pos.service'
import {
  getStaffStoreOrder,
  getStaffStoreOrdersProblem,
  listStaffStoreOrders,
  listStoreOrderCashCollections,
} from '@/app/services/store-orders.service'

export type CashDepositTarget =
  | { type: 'general' }
  | { type: 'pickup'; orderNumber: string }
  | { type: 'delivery'; collectionId: number }

export interface CashDepositModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentCashInRegister: number
  depositAmount: string
  setDepositAmount: (amount: string) => void
  depositConcept: string
  setDepositConcept: (concept: string) => void
  isSubmitting?: boolean
  handleCashDeposit: (target: CashDepositTarget) => void
  setShowCashDeposit: (show: boolean) => void
  cashCapacity: CashCapacity | null
  sucursalId?: number
}

export function CashDepositModal({
  open,
  onOpenChange,
  currentCashInRegister,
  depositAmount,
  setDepositAmount,
  depositConcept,
  setDepositConcept,
  isSubmitting,
  handleCashDeposit,
  setShowCashDeposit,
  cashCapacity,
  sucursalId,
}: CashDepositModalProps) {
  const [targetType, setTargetType] =
    useState<CashDepositTarget['type']>('general')
  const [orderNumber, setOrderNumber] = useState('')
  const [collectionId, setCollectionId] = useState('')
  const [orderPage, setOrderPage] = useState(1)
  const [collectionPage, setCollectionPage] = useState(1)
  const orders = useQuery({
    queryKey: ['cash-deposit-pickup-orders', sucursalId, orderPage],
    queryFn: () =>
      listStaffStoreOrders({
        status: 'COMPLETED',
        fulfillment_type: 'PICKUP',
        fulfillment_status: 'READY_FOR_PICKUP',
        cash_pending: true,
        sucursal_id: sucursalId,
        page: orderPage,
        per_page: 25,
      }),
    enabled: open && targetType === 'pickup',
  })
  const selectedOrder = useQuery({
    queryKey: ['cash-deposit-pickup-order', orderNumber],
    queryFn: () => getStaffStoreOrder(orderNumber),
    enabled: open && targetType === 'pickup' && orderNumber.length > 0,
  })
  const collections = useQuery({
    queryKey: ['cash-deposit-collections', sucursalId, collectionPage],
    queryFn: () =>
      listStoreOrderCashCollections({
        sucursal_id: sucursalId,
        page: collectionPage,
        per_page: 25,
      }),
    enabled: open && targetType === 'delivery',
  })
  const availableCollections =
    collections.data?.collections.filter((row) =>
      ['IN_TRANSIT', 'PARTIALLY_REMITTED'].includes(row.status),
    ) ?? []
  const selectedCollection = availableCollections.find(
    (row) => row.id === Number(collectionId),
  )
  const collectionRemaining = selectedCollection
    ? Number(selectedCollection.amount) -
      Number(selectedCollection.remitted_amount) -
      Number(selectedCollection.shortfall_amount)
    : 0
  const pickupDue = selectedOrder.data?.sale?.payments?.find(
    (row) => row.method === 'cash' && row.status === 'PENDING',
  )
  const pickupValid = Boolean(
    selectedOrder.data &&
    selectedOrder.data.order_number === orderNumber &&
    selectedOrder.data.status === 'COMPLETED' &&
    selectedOrder.data.fulfillment_status === 'READY_FOR_PICKUP' &&
    pickupDue,
  )
  const remainingCapacity = Number(cashCapacity?.remaining_amount || 0)
  const parsedDeposit = Number(depositAmount || 0)
  const cashInflowBlocked = cashCapacity?.can_receive_cash === false
  const depositToRegister =
    targetType === 'pickup' ? Number(pickupDue?.amount || 0) : parsedDeposit
  const willExceedLimit =
    Boolean(cashCapacity) &&
    !cashInflowBlocked &&
    depositToRegister > remainingCapacity
  const validTarget =
    targetType === 'general'
      ? Boolean(depositConcept.trim())
      : targetType === 'pickup'
        ? pickupValid && parsedDeposit >= Number(pickupDue?.amount || 0)
        : Boolean(
            selectedCollection &&
            collectionRemaining > 0 &&
            parsedDeposit <= collectionRemaining,
          )

  const reset = () => {
    setShowCashDeposit(false)
    setDepositAmount('')
    setDepositConcept('')
    setTargetType('general')
    setOrderNumber('')
    setCollectionId('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:max-w-lg sm:p-6">
        <DialogHeader>
          <DialogTitle>Ingreso de Efectivo</DialogTitle>
          <DialogDescription className="sr-only">
            Ingresar efectivo a la caja
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-4">
          <div className="border-primary/20 bg-card rounded-lg border p-4">
            <p className="text-card-description text-sm">
              Efectivo actual en caja:
            </p>

            <p className="text-card-foreground mt-1 break-words text-2xl font-semibold">
              ${currentCashInRegister.toFixed(2)} MXN
            </p>
          </div>

          {cashCapacity && (
            <div
              className={`rounded-lg border p-3 ${cashInflowBlocked ? 'border-red-500/30 bg-red-500/10' : willExceedLimit ? 'border-amber-500/30 bg-amber-500/10' : 'border-primary/20 bg-card'}`}
            >
              <p
                className={`text-sm ${cashInflowBlocked ? 'text-red-400' : willExceedLimit ? 'text-amber-700' : 'text-card-foreground'}`}
              >
                {cashInflowBlocked
                  ? `La caja excede su límite por $${Number(cashCapacity.excess_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN. Registre un retiro antes de otro ingreso.`
                  : willExceedLimit
                    ? 'Este ingreso superará el límite y bloqueará nuevas entradas de efectivo hasta realizar un retiro.'
                    : `Capacidad disponible: $${remainingCapacity.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`}
              </p>
            </div>
          )}

          <div className="min-w-0 space-y-2">
            <label
              htmlFor="cash-deposit-target"
              className="text-card-foreground text-sm font-medium"
            >
              Tipo de ingreso
            </label>
            <Select
              value={targetType}
              onValueChange={(value) => {
                setTargetType(value as CashDepositTarget['type'])
                setDepositAmount('')
                setOrderNumber('')
                setCollectionId('')
              }}
            >
              <SelectTrigger id="cash-deposit-target" className="w-full min-w-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                <SelectItem value="general">Ingreso general</SelectItem>
                <SelectItem value="pickup">
                  Aplicar a pedido para recolección
                </SelectItem>
                <SelectItem value="delivery">
                  Rendición de pedido a domicilio
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {targetType === 'pickup' && (
            <div className="min-w-0 space-y-2">
              <p className="text-muted-foreground text-xs">
                Sólo aparecen pedidos para recoger con ticket y efectivo pendiente.
                Los pedidos a domicilio se rinden después de que el repartidor
                registra el cobro.
              </p>
              <label
                htmlFor="cash-deposit-order"
                className="text-card-foreground text-sm font-medium"
              >
                Pedido con ticket
              </label>
              <Select
                value={orderNumber}
                onValueChange={(value) => {
                  setOrderNumber(value)
                  setDepositAmount('')
                }}
                disabled={
                  orders.isPending || orders.isError || !orders.data?.orders.length
                }
              >
                <SelectTrigger id="cash-deposit-order" className="w-full min-w-0">
                  <SelectValue
                    placeholder={
                      orders.isPending
                        ? 'Cargando pedidos...'
                        : 'Selecciona un pedido'
                    }
                  />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                  {orders.data?.orders.map((row) => (
                    <SelectItem
                      key={row.order_number}
                      value={row.order_number}
                      className="min-w-0 [&>span:last-child]:min-w-0 [&>span:last-child]:truncate"
                    >
                      {row.order_number} · {row.customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {orders.isError && (
                <p className="text-sm text-red-600">
                  {getStaffStoreOrdersProblem(
                    orders.error,
                    'No se pudieron cargar los pedidos.',
                  ).message}
                </p>
              )}
              {orders.isSuccess && orders.data.orders.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  No hay pedidos de recolección con efectivo pendiente.
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={orderPage <= 1}
                  onClick={() => {
                    setOrderNumber('')
                    setDepositAmount('')
                    setOrderPage((page) => page - 1)
                  }}
                >
                  Anterior
                </Button>
                <span>
                  Página {orderPage} de {orders.data?.pagination.last_page || 1}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    orderPage >= (orders.data?.pagination.last_page || 1)
                  }
                  onClick={() => {
                    setOrderNumber('')
                    setDepositAmount('')
                    setOrderPage((page) => page + 1)
                  }}
                >
                  Siguiente
                </Button>
              </div>
              {selectedOrder.isLoading && (
                <p className="text-sm">Validando pedido…</p>
              )}
              {selectedOrder.data && !pickupValid && (
                <p className="text-sm text-amber-700">
                  Este pedido no tiene efectivo pendiente de cobro.
                </p>
              )}
              {pickupDue && (
                <p className="text-sm">
                  Ticket: {selectedOrder.data?.sale?.ticket_number}. Saldo: $
                  {pickupDue.amount} MXN. El cambio no se deposita.
                </p>
              )}
            </div>
          )}

          {targetType === 'delivery' && (
            <div className="min-w-0 space-y-2">
              <label
                htmlFor="cash-deposit-collection"
                className="text-card-foreground text-sm font-medium"
              >
                Cobro del repartidor
              </label>
              <Select
                value={collectionId}
                onValueChange={(value) => {
                  setCollectionId(value)
                  setDepositAmount('')
                }}
                disabled={
                  collections.isPending ||
                  collections.isError ||
                  availableCollections.length === 0
                }
              >
                <SelectTrigger
                  id="cash-deposit-collection"
                  className="w-full min-w-0"
                >
                  <SelectValue
                    placeholder={
                      collections.isPending
                        ? 'Cargando cobros...'
                        : 'Selecciona un cobro'
                    }
                  />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                  {availableCollections.map((row) => (
                    <SelectItem
                      key={row.id}
                      value={String(row.id)}
                      className="min-w-0 [&>span:last-child]:min-w-0 [&>span:last-child]:truncate"
                    >
                      #{row.id} · {row.order_number} · pendiente $
                      {(
                        Number(row.amount) -
                        Number(row.remitted_amount) -
                        Number(row.shortfall_amount)
                      ).toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {collections.isError && (
                <p className="text-sm text-red-600">
                  No se pudieron cargar los cobros.
                </p>
              )}
              {collections.isSuccess && availableCollections.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  Aún no hay cobros domiciliarios registrados para rendir.
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={collectionPage <= 1}
                  onClick={() => {
                    setCollectionId('')
                    setDepositAmount('')
                    setCollectionPage((page) => page - 1)
                  }}
                >
                  Anterior
                </Button>
                <span>
                  Página {collectionPage} de{' '}
                  {collections.data?.pagination.last_page || 1}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    collectionPage >=
                    (collections.data?.pagination.last_page || 1)
                  }
                  onClick={() => {
                    setCollectionId('')
                    setDepositAmount('')
                    setCollectionPage((page) => page + 1)
                  }}
                >
                  Siguiente
                </Button>
              </div>
              {selectedCollection && (
                <p className="text-sm">
                  Pendiente de rendir: ${collectionRemaining.toFixed(2)} MXN.
                  Puedes entregar una parte.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-card-foreground text-sm font-medium">
              {targetType === 'pickup'
                ? 'Efectivo entregado por el cliente'
                : 'Monto a ingresar'}
              : <span className="text-red-600">*</span>
            </label>
            <Input
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>

          {targetType === 'general' && (
            <div className="space-y-2">
              <label className="text-card-foreground text-sm font-medium">
                Concepto: <span className="text-red-600">*</span>
              </label>
              <Input
                value={depositConcept}
                onChange={(e) => setDepositConcept(e.target.value)}
                placeholder="Ej: Fondo de caja, reposición..."
              />
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              onClick={() =>
                handleCashDeposit(
                  targetType === 'general'
                    ? { type: 'general' }
                    : targetType === 'pickup'
                      ? { type: 'pickup', orderNumber }
                      : {
                          type: 'delivery',
                          collectionId: Number(collectionId),
                        },
                )
              }
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:flex-1"
              disabled={
                !depositAmount ||
                parseFloat(depositAmount) <= 0 ||
                cashInflowBlocked ||
                willExceedLimit ||
                !validTarget ||
                isSubmitting
              }
            >
              {isSubmitting ? 'Procesando...' : 'Confirmar Ingreso'}
            </Button>
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={reset}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
